import * as Sentry from '@sentry/browser';
import {sanitizeExtras} from 'src/internal/errors/sentry/utils';

import type {
  SentryHub,
  SentryHubOptions,
} from 'src/internal/errors/sentry/types';

const hubs = new Map<string, SentryHub>();

export function createHub(dsn: string, options: SentryHubOptions): SentryHub {
  // "fetch" detection is buggy in our Sentry version, so make it ourselves.
  const transport =
    typeof fetch === 'function'
      ? Sentry.Transports.FetchTransport
      : Sentry.Transports.XHRTransport;

  const client = new Sentry.BrowserClient({
    dsn,
    release: options.release,
    enabled: options.enabled ?? true,
    integrations: options.integrations ?? [],
    beforeSend: options.beforeSend,
    transport,
  });
  const hub = new Sentry.Hub(client);
  const {user, tags, extras} = options.initialScope ?? {};

  hub.configureScope((scope) => {
    if (user) {
      scope.setUser(user);
    }
    if (tags) {
      scope.setTags(tags);
    }
    if (extras) {
      scope.setExtras(sanitizeExtras(extras));
    }

    // 'error' is the implicit default that sentry uses, but we're setting it
    // here as the explicit default so that integrations have reliable access
    // to the level per event.
    scope.setLevel(Sentry.Severity.Error);
  });

  return hub;
}

export function getOrCreate(dsn: string, options: SentryHubOptions): SentryHub {
  const existing = hubs.get(dsn);
  if (existing) {
    return existing;
  }

  const hub = createHub(dsn, options);
  hubs.set(dsn, hub);
  return hub;
}
